/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

/* eslint-disable react/prop-types */
/* eslint-disable react/jsx-props-no-spreading */
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { saveAs } from "file-saver";
import uploadToS3, { getProcessState } from "../services/uploadToS3Service";
import { getPrompts, getConnectPrompts } from "../services/promptServices";
import objectToCSV from "../utility/csvHelper";
import { getPromptTypeSelectArr, savePromptsSettings } from "../utility/lookupHelper";
import ProgressOverlay from "../../../components/Common/ProgressOverlay";
import { ShowSnackbar } from "../../../components/Common";
import { useRef } from "react";
import PromptEditForm from "./PromptEditForm";
import { columnsDefinition, getPromptIvrId, getPromptIndex, getNameSpaceAndPromptId } from "./utils";
import { getComparator, stableSort } from "../../../utility/tableSort";
import PromptsList from "./PromptsList";
import { deletePrompt } from "../services/promptServices";
import { ConfirmDialog } from "../../../components/ConfirmDialog/ConfirmDialog";

const noMessage = {
	type: "none",
	text: "",
};

const promptTypeSelectArr = getPromptTypeSelectArr();

let defaultPromptType = "";
if (promptTypeSelectArr && promptTypeSelectArr.length > 0) {
	defaultPromptType = promptTypeSelectArr[0].value;
}

// ----------------------------------------------------------------------------
// << PromptsTable Component >> -----------------------------------------------
// ----------------------------------------------------------------------------
const PromptsTable = ({ settings, languages, namespaces }) => {
	const [config, setConfig] = useState({});
	const [prompts, setPrompts] = useState([]);
	const [message, setMessage] = useState(noMessage);
	const [isLoading, setIsLoading] = useState(false);
	const [connectPromptsList, setConnectPromptsList] = useState([]);
	const [editPrompt, setEditPrompt] = useState(undefined);
	const [searchTerm, setSearchTerm] = useState("");
	const [filterType, setFilterType] = useState("All");
	const [sortConfig, setSortConfig] = useState({ order: "asc", orderBy: "promptId" });
	const [deletingPrompt, setDeletingPrompt] = useState(undefined);
	const [fileProcessingState, setFileProcessingState] = useState("");

	const fileElement = useRef();

	useEffect(() => {
		setConfig(settings);
	}, [settings]);

	const getConnectPromptName = (arn, type) => {
		let arnName;
		if (type === "audio" && arn !== undefined) {
			arnName = connectPromptsList.find((e) => e.Arn === arn);
		}
		return arnName !== undefined ? arnName.Name : arn;
	};

	const filterPrompts = (prompts, searchTerm) => {
		const searchTermUpper = searchTerm.toUpperCase();
		const filteredPrompts = prompts?.filter(
			(prompt) =>
				(filterType === "All" || prompt.type === filterType) &&
				(getConnectPromptName(prompt.prompt, prompt.type)?.toUpperCase().includes(searchTermUpper) ||
					prompt?.promptId?.toUpperCase().includes(searchTermUpper)),
		);
		return filteredPrompts;
	};

	const filteredPrompts = filterPrompts(prompts, searchTerm);

	// Show message in snackbar
	const showMessage = (type, text) => {
		setMessage({ type, text });
	};

	const onSnackBarClose = () => {
		setMessage((cur) => ({ ...cur, text: "" }));
	};

	const onNewPromptAdd = () => {
		setEditPrompt({ language: config?.language, namespace: config?.namespace, type: defaultPromptType });
	};

	const loadConnectPrompts = async () => {
		let connectPrompts = await getConnectPrompts();
		connectPrompts = connectPrompts.sort((a, b) => (a.Name > b.Name ? 1 : -1));
		setConnectPromptsList(connectPrompts);
	};

	useEffect(() => {
		const initPrompts = async () => {
			setIsLoading(true);
			const promptsTab = await getPrompts(config?.language, config?.namespace);
			await loadConnectPrompts();
			savePromptsSettings(config);
			setPrompts(promptsTab);
			onSnackBarClose();
			setIsLoading(false);
		};

		// Get prompt data from server to initialize the prompts table
		if (config?.language && config?.namespace) {
			initPrompts();
		}
	}, [config]);

	const checkForProcessState = async (fileName, retryCnt = 1, retry = 1) => {
		if (retry <= retryCnt) {
			const processState = await getProcessState(fileName);
			setFileProcessingState(processState);
			if (processState === "unprocessed" || processState === "inprogress") {
				setTimeout(async () => {
					await checkForProcessState(fileName, retryCnt, retry + 1);
				}, 2000);
			} else if (processState === "failed") {
				showMessage("error", "Failed to process uploaded file.");
			} else if (processState === "completed") {
				await refreshPrompts(true);
			}
		}
	};

	// Upload selected file to S3
	const uploadFile = (event) => {
		const { files } = event.target;
		const inputFile = files[0];

		if (inputFile) {
			setIsLoading(true);
			const filename = inputFile.name.replaceAll(/[\s()]+/g, "");

			const reader = new FileReader();
			reader.onload = async () => {
				const pathName = `upload/${filename}`;
				const fileContent = reader.result;

				const resp = await uploadToS3(pathName, fileContent);

				if (resp.status === "success") {
					showMessage("info", `${filename} has been uploaded, processing the file in the background.`);
					setFileProcessingState("unprocessed");
					await checkForProcessState(pathName, 5, 1);
				} else {
					showMessage("error", `Failed to upload ${filename}.`);
				}
				setIsLoading(false);
			};
			reader.readAsText(inputFile);
		}
		event.target.value = null;
	};

	const refreshPrompts = async (silent = false) => {
		!silent && setIsLoading(true);
		const promptsTab = await getPrompts(config?.language, config?.namespace);
		await loadConnectPrompts();
		setPrompts(promptsTab);
		!silent && showMessage("success", "Successfully loaded prompts.");
		!silent && setIsLoading(false);
	};

	const exportFunc = () => {
		const promptsWithoutNewLine = prompts.map((prompt) => ({ ...prompt, prompt: prompt.prompt?.replaceAll("\n", "") }));
		const csvStr = objectToCSV(promptsWithoutNewLine, columnsDefinition.length);
		const blob = new Blob([csvStr], { type: "text/plain;charset=utf-8" });
		saveAs(blob, "prompts.csv");
	};

	const openFileDialog = () => {
		fileElement.current?.click();
	};

	const onConfigSelectionChange = (newConfig) => {
		setConfig(newConfig);
	};

	const onConfirmPromptDelete = async (confirmStatus) => {
		if (confirmStatus) {
			try {
				setIsLoading(true);
				const oldPromptId = getNameSpaceAndPromptId(deletingPrompt);
				const resp = await deletePrompt(deletingPrompt?.language, oldPromptId);
				if (resp.status === "success") {
					const promptsTemp = [...prompts];
					const index = getPromptIndex(promptsTemp, deletingPrompt);
					promptsTemp.splice(index, 1);
					setPrompts([...promptsTemp]);
					showMessage("success", `Successfully removed ${getPromptIvrId(deletingPrompt)}.`);
				} else {
					showMessage("error", `Failed to remove ${getPromptIvrId(deletingPrompt)}.`);
				}
			} catch (error) {
				showMessage("error", `Failed to remove ${getPromptIvrId(deletingPrompt)}`);
			} finally {
				setDeletingPrompt(undefined);
				setIsLoading(false);
			}
		} else {
			setDeletingPrompt(undefined);
		}
	};

	const onDeletePrompt = (prompt) => {
		setDeletingPrompt(prompt);
	};

	return (
		<div className="prompts-table w-100 mt-4">
			{isLoading && <ProgressOverlay />}
			<PromptsList
				languages={languages}
				namespaces={namespaces}
				onRefresh={refreshPrompts}
				onExport={exportFunc}
				setFilterTerm={setSearchTerm}
				config={config}
				onFileImport={openFileDialog}
				onSelect={onConfigSelectionChange}
				onPromptAdd={onNewPromptAdd}
				prompts={stableSort(filteredPrompts, getComparator(sortConfig?.order, sortConfig?.orderBy))}
				getConnectPromptName={getConnectPromptName}
				setEditPrompt={setEditPrompt}
				showMessage={showMessage}
				deletePrompt={onDeletePrompt}
				fileProcessingState={fileProcessingState}
				setFilterType={setFilterType}
				filterType={filterType}
				setSortConfig={setSortConfig}
				sortConfig={sortConfig}
			/>
			<input
				type="file"
				ref={fileElement}
				id="filedialog"
				accept=".csv"
				onChange={uploadFile}
				style={{ display: "none" }}
			/>
			<PromptEditForm
				activePrompt={editPrompt}
				allPrompts={prompts}
				showMessage={showMessage}
				connectPromptsList={connectPromptsList}
				isOpen={!!editPrompt}
				onEditComplete={(prompt) => {
					const oldList = prompts.map((p) => (p.promptId === prompt.promptId ? prompt : p));
					setPrompts(oldList);
					setEditPrompt(undefined);
				}}
				onCreateComplete={(prompt) => {
					setPrompts([...prompts, prompt]);
					setEditPrompt(undefined);
				}}
				onPromptClose={() => setEditPrompt(undefined)}
			/>
			<ShowSnackbar severity={message.type} message={message.text} onClose={onSnackBarClose} />
			<ConfirmDialog
				title="Are you sure you want to delete this prompt?"
				confirmOpen={!!deletingPrompt}
				message="Selected prompt will be deleted permanently."
				onClose={onConfirmPromptDelete}
			/>
		</div>
	);
};

PromptsTable.propTypes = {
	settings: PropTypes.shape({
		language: PropTypes.string,
		namespace: PropTypes.string,
	}).isRequired,
};

export default PromptsTable;
